import * as React from "react";
import { toast } from "sonner";
import { Send, AlertTriangle, Megaphone, BarChart2, Sparkles, Clock, X } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import PollComposerDialog from "./PollComposerDialog";
import RecognitionComposerDialog from "./RecognitionComposerDialog";
import ScheduleSendDialog from "./ScheduleSendDialog";

/**
 * Composer — plain textarea. Enter sends, Shift+Enter newline. Adds
 * priority selector + requires-ack toggle for announcement channels or
 * managers who need it. Priority urgent/emergency require comms.manage.
 */
const MAX_BODY = 4000;

const PRIORITIES = [
  { v: "normal", label: "Normal", hint: "" },
  { v: "important", label: "Important", hint: "Distinct in the rail" },
  { v: "urgent", label: "Urgent", hint: "Bypasses off-shift quiet · logged" },
  { v: "emergency", label: "Emergency", hint: "Bypasses off-shift quiet · logged" },
];

export default function Composer({ companyId, conversation, disabled, disabledReason, onSent, canManage, quoteTarget, onClearQuote }) {
  const [body, setBody] = React.useState("");
  const [sending, setSending] = React.useState(false);
  const [priority, setPriority] = React.useState("normal");
  const [requiresAck, setRequiresAck] = React.useState(false);
  const [mentionState, setMentionState] = React.useState({ open: false, q: "", results: [], start: -1 });
  const [pickedMentions, setPickedMentions] = React.useState([]);
  const [pollOpen, setPollOpen] = React.useState(false);
  const [recognitionOpen, setRecognitionOpen] = React.useState(false);
  const [scheduleOpen, setScheduleOpen] = React.useState(false);
  const [draftLoaded, setDraftLoaded] = React.useState(false);
  const textareaRef = React.useRef(null);

  const isAnnouncement = conversation?.kind === "announcement";

  React.useEffect(() => {
    setBody(""); setPickedMentions([]);
    setMentionState({ open: false, q: "", results: [], start: -1 });
    setPriority(isAnnouncement ? "important" : "normal");
    setRequiresAck(!!isAnnouncement);
    setDraftLoaded(false);
  }, [conversation?.id, isAnnouncement]);

  // Load server-side draft on open
  React.useEffect(() => {
    if (!conversation?.id || draftLoaded || disabled) return;
    api.get(`/companies/${companyId}/comms/conversations/${conversation.id}/draft`)
      .then(({ data }) => {
        if (data?.body && !body) setBody(data.body);
      })
      .catch(() => {})
      .finally(() => setDraftLoaded(true));
  }, [conversation?.id, companyId, draftLoaded, disabled, body]);

  // Debounced draft autosave
  React.useEffect(() => {
    if (!conversation?.id || !draftLoaded) return;
    const h = setTimeout(() => {
      api.put(`/companies/${companyId}/comms/conversations/${conversation.id}/draft`, { body })
        .catch(() => {});
    }, 500);
    return () => clearTimeout(h);
  }, [body, conversation?.id, companyId, draftLoaded]);

  const onChange = (e) => {
    const val = e.target.value;
    setBody(val);
    const caret = e.target.selectionStart ?? val.length;
    const uptoCaret = val.slice(0, caret);
    const at = uptoCaret.lastIndexOf("@");
    if (at === -1 || (at > 0 && !/[\s\n]/.test(uptoCaret[at - 1]))) {
      setMentionState({ open: false, q: "", results: [], start: -1 }); return;
    }
    const q = uptoCaret.slice(at + 1);
    if (q.length > 32 || /\s/.test(q)) {
      setMentionState({ open: false, q: "", results: [], start: -1 }); return;
    }
    setMentionState((prev) => ({ ...prev, open: true, q, start: at }));
  };

  React.useEffect(() => {
    if (!mentionState.open) return () => {};
    const h = setTimeout(async () => {
      try {
        const { data } = await api.get(`/companies/${companyId}/search/members`, {
          params: { q: mentionState.q, limit: 6 },
        });
        setMentionState((prev) => prev.open ? { ...prev, results: data.members || [] } : prev);
      } catch { /* ignore */ }
    }, 90);
    return () => clearTimeout(h);
  }, [mentionState.open, mentionState.q, companyId]);

  const insertMention = (member) => {
    const val = body;
    const before = val.slice(0, mentionState.start);
    const afterCaret = val.slice((textareaRef.current?.selectionStart) ?? val.length);
    const token = `@${member.name}`;
    const next = `${before}${token} ${afterCaret}`;
    setBody(next);
    setPickedMentions((prev) => prev.some((m) => m.id === member.id) ? prev : [...prev, { id: member.id, name: member.name }]);
    setMentionState({ open: false, q: "", results: [], start: -1 });
    requestAnimationFrame(() => {
      const el = textareaRef.current;
      if (el) {
        el.focus();
        const newCaret = before.length + token.length + 1;
        el.setSelectionRange(newCaret, newCaret);
      }
    });
  };

  async function send() {
    if (sending || disabled) return;
    const val = body.trim();
    if (!val) return;
    if (val.length > MAX_BODY) { toast.error(`Messages are limited to ${MAX_BODY} characters.`); return; }
    setSending(true);
    const tempId = `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const now = new Date().toISOString();
    const active = pickedMentions.filter((m) => val.includes(`@${m.name}`));
    const currentUser = window.__fableUser || {};
    window.dispatchEvent(new CustomEvent("fable:optimistic-message", {
      detail: {
        conversation_id: conversation.id,
        message: {
          id: tempId, conversation_id: conversation.id,
          sender_id: currentUser.id || null,
          sender_name: currentUser.name || "You",
          body: val, created_at: now, edited_at: null,
          priority, requires_ack: requiresAck,
          mention_user_ids: active.map((m) => m.id),
          _pending: true,
        },
      },
    }));
    setBody(""); setPickedMentions([]);
    try {
      const { data } = await api.post(
        `/companies/${companyId}/comms/conversations/${conversation.id}/messages`,
        { body: val, mention_user_ids: active.map((m) => m.id),
          priority, requires_ack: requiresAck,
          quoted_message_id: quoteTarget?.id || null },
      );
      window.dispatchEvent(new CustomEvent("fable:optimistic-swap", {
        detail: { conversation_id: conversation.id, temp_id: tempId, message: data },
      }));
      // Clear the server-side draft on successful send
      api.put(`/companies/${companyId}/comms/conversations/${conversation.id}/draft`, { body: "" })
        .catch(() => {});
      onSent && onSent(data);
    } catch (e) {
      window.dispatchEvent(new CustomEvent("fable:optimistic-fail", {
        detail: { conversation_id: conversation.id, temp_id: tempId },
      }));
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not send");
    } finally { setSending(false); }
  }

  const onKey = (e) => {
    if (mentionState.open && (e.key === "ArrowDown" || e.key === "ArrowUp" || e.key === "Tab")) {
      if (e.key === "Tab") e.preventDefault();
      return;
    }
    if (e.key === "Enter" && !e.shiftKey && !e.altKey && !e.metaKey && !e.ctrlKey) {
      if (mentionState.open && mentionState.results.length > 0) {
        e.preventDefault(); insertMention(mentionState.results[0]); return;
      }
      e.preventDefault(); send();
    }
    if (e.key === "Escape" && mentionState.open) {
      setMentionState({ open: false, q: "", results: [], start: -1 });
    }
  };

  const priorityChoices = canManage ? PRIORITIES : PRIORITIES.slice(0, 2);
  const priorityTone = priority === "urgent" || priority === "emergency" ? "coral"
                      : priority === "important" ? "amber" : "";

  return (
    <div className="border-t border-brand-rule bg-card px-4 py-3" data-testid="comms-composer">
      {disabled && (
        <div className="mb-2 text-[11px] text-brand-coral" data-testid="comms-composer-disabled">
          {disabledReason || "You can't send messages here."}
        </div>
      )}
      {quoteTarget && (
        <div className="mb-2 flex items-start gap-2 rounded-md border-l-2 border-brand-violet bg-brand-hush px-2.5 py-1.5"
             data-testid="comms-composer-quote">
          <div className="min-w-0 flex-1">
            <div className="text-[10.5px] uppercase tracking-wider text-brand-muted">
              Replying to {quoteTarget.sender_name}
            </div>
            <div className="text-[12px] text-brand-ink truncate">
              {(quoteTarget.body || "").slice(0, 180)}
            </div>
          </div>
          <button
            type="button"
            onClick={onClearQuote}
            className="btn-ghost h-6 w-6 !p-0"
            aria-label="Clear quote"
            data-testid="comms-composer-quote-clear"
          >
            <X size={11} />
          </button>
        </div>
      )}
      <div className="relative">
        <textarea
          ref={textareaRef}
          className="field-input py-2 min-h-[46px] max-h-[220px] resize-y w-full"
          placeholder={disabled ? "" : (
            isAnnouncement ? "Post an announcement — attention will be gathered" :
            `Message ${conversation.kind === "channel" ? `#${conversation.slug}` : (conversation.counterpart?.name || "")}`
          )}
          value={body}
          onChange={onChange}
          onKeyDown={onKey}
          disabled={disabled || sending}
          data-testid="comms-composer-input"
          maxLength={MAX_BODY + 20}
          aria-label="Message body"
        />
        {mentionState.open && mentionState.results.length > 0 && (
          <div
            className="absolute left-0 right-auto bottom-full mb-2 min-w-[240px] bg-card border border-brand-rule rounded-md shadow-card py-1 z-30"
            data-testid="comms-mention-menu"
          >
            <div className="px-3 pt-1.5 pb-1 text-[10.5px] uppercase tracking-wider text-brand-muted">Members</div>
            {mentionState.results.map((m) => (
              <button
                key={m.id} type="button"
                onMouseDown={(e) => { e.preventDefault(); insertMention(m); }}
                className="w-full flex items-center gap-2 px-3 py-1.5 text-left text-[12.5px] hover:bg-brand-hush"
                data-testid={`comms-mention-option-${m.id}`}
              >
                <span className="h-5 w-5 rounded-full bg-brand-violet text-white text-[10px] font-bold flex items-center justify-center">
                  {(m.name || "?").charAt(0).toUpperCase()}
                </span>
                <span className="font-medium text-brand-ink">{m.name}</span>
                <span className="text-brand-muted truncate">{m.email}</span>
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="mt-2 flex items-center gap-2 flex-wrap">
        {(canManage || isAnnouncement) && (
          <select
            className={`field-input !h-8 !w-36 text-[11.5px] ${priorityTone === "coral" ? "!border-brand-coral" : priorityTone === "amber" ? "!border-amber-500" : ""}`}
            value={priority}
            onChange={(e) => setPriority(e.target.value)}
            data-testid="comms-composer-priority"
            aria-label="Message priority"
          >
            {priorityChoices.map((p) => <option key={p.v} value={p.v}>{p.label}</option>)}
          </select>
        )}
        {(canManage || isAnnouncement) && (
          <label className="inline-flex items-center gap-1.5 text-[11.5px] text-brand-muted cursor-pointer">
            <input
              type="checkbox" checked={requiresAck}
              onChange={(e) => setRequiresAck(e.target.checked)}
              data-testid="comms-composer-ack"
            />
            Requires ack
          </label>
        )}
        <div className="flex-1 text-[10.5px] text-brand-faint">
          {mentionState.open
            ? "@ to mention · ↵ insert first · esc cancel"
            : (priority === "urgent" || priority === "emergency"
                ? "Off-shift recipients will still be alerted — the bypass is logged."
                : "↵ send · shift+↵ newline")}
        </div>
        <button
          type="button"
          onClick={send}
          disabled={disabled || sending || !body.trim()}
          className="btn-violet"
          data-testid="comms-composer-send"
          aria-label="Send message"
        >
          {sending ? "…" : <>Send <Send size={13} /></>}
        </button>
      </div>
      {/* Secondary actions row */}
      {!disabled && conversation?.kind !== "announcement" && (
        <div className="mt-1.5 flex items-center gap-1 text-brand-muted">
          <button
            type="button"
            onClick={() => setPollOpen(true)}
            className="btn-ghost h-7 text-[11px]"
            data-testid="comms-composer-new-poll"
            title="Create poll"
          >
            <BarChart2 size={11} /> Poll
          </button>
          <button
            type="button"
            onClick={() => setRecognitionOpen(true)}
            className="btn-ghost h-7 text-[11px]"
            data-testid="comms-composer-new-recognition"
            title="Recognise a teammate"
          >
            <Sparkles size={11} /> Recognition
          </button>
          <button
            type="button"
            onClick={() => setScheduleOpen(true)}
            disabled={!body.trim()}
            className="btn-ghost h-7 text-[11px]"
            data-testid="comms-composer-schedule"
            title="Schedule send"
          >
            <Clock size={11} /> Schedule
          </button>
        </div>
      )}
      <PollComposerDialog
        open={pollOpen}
        onOpenChange={setPollOpen}
        companyId={companyId}
        conversation={conversation}
      />
      <RecognitionComposerDialog
        open={recognitionOpen}
        onOpenChange={setRecognitionOpen}
        companyId={companyId}
        conversation={conversation}
      />
      <ScheduleSendDialog
        open={scheduleOpen}
        onOpenChange={setScheduleOpen}
        companyId={companyId}
        conversation={conversation}
        body={body}
        priority={priority}
        onScheduled={() => { setBody(""); setScheduleOpen(false); }}
      />
    </div>
  );
}
